import {
  FileOutlined,
  RollbackOutlined,
  SendOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { AccordionItem, Link } from '@mm/components';
import { EmailOpenIcon, RouteIcon } from '@mm/icons';
import { ClickIcon } from '@mm/icons/general/ClickIcon';
import { useViewActions } from '@mm/state/actions/viewActions';
import {
  ContactCustomerJourney,
  ContactCustomerJourneyMessage,
} from '@mm/state/interfaces/contacts';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { BotBuilderRoutes } from '../../../../../../../routes/bot-builder-routes';
import styles from './customerJourney.module.css';

type Props = {
  customerJourney?: ContactCustomerJourney;
};
type ContactCustomerJourneyItem = ContactCustomerJourney[0];
const CustomerJourneyContent = (props: {
  customerJourney: ContactCustomerJourney;
}) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { hideSlideoutMenu } = useViewActions();
  const { customerJourney } = props;
  if (!customerJourney.length) {
    return null;
  }
  const closeSlideout = () => {
    hideSlideoutMenu();
  };
  const emails = new Map(
    customerJourney
      .filter(item => item.kind === 'message')
      .map(item => [
        item.time,
        (item as ContactCustomerJourneyMessage).structure.subject,
      ]),
  );
  const firstUrlVisitedTimestamp = customerJourney.filter(
    item => item.kind === 'url_visited',
  )[0]?.time;
  const contentBuilder = (item: ContactCustomerJourneyItem) => {
    switch (item.kind) {
      case 'url_visited':
        const showVisitedAgain =
          item.structure.newSession && item.time !== firstUrlVisitedTimestamp;
        return (
          <>
            <div>
              {showVisitedAgain ? <RollbackOutlined /> : <FileOutlined />}
            </div>
            <div>
              {showVisitedAgain ? 'Visited website again' : 'Visited page'}
              <br />
              <Link
                capitalize={false}
                to={{
                  pathname: BotBuilderRoutes.contacts.getLink({ chatbotId }),
                  state: {
                    query: {
                      attributes: { urls_visited: [item.structure.url] },
                    },
                  },
                }}
                onLinkClick={() => closeSlideout()}
              >
                {item.structure.url}
              </Link>
            </div>
          </>
        );

      case 'read':
        return (
          <>
            <div>
              <EmailOpenIcon color="#595959" />
            </div>
            <div>
              Opened an email <strong>{item.structure.subject}</strong>
            </div>
          </>
        );
      case 'link_clicked':
        const emailSubject = emails.get(Number(item.structure.emailTimestamp));
        if (!emailSubject) return null;
        return (
          <>
            <div>
              <ClickIcon />
            </div>
            <div>
              Clicked on a link in the email <strong>{emailSubject}</strong>
            </div>
          </>
        );
      case 'message':
        return (
          <>
            <div>
              <SendOutlined />
            </div>
            <div>
              Email sent <strong>{item.structure.subject}</strong>
            </div>
          </>
        );
      case 'new_xray_contact':
        if (!item.structure.name) return null;
        return (
          <>
            <div>
              <UserAddOutlined />
            </div>
            <div>
              <strong>Meet your new X-Ray Lead</strong>
              <br />
              Website visitor <strong>{item.structure.name}</strong> was
              detected
            </div>
          </>
        );
    }
  };
  return (
    <>
      {customerJourney.map(item => {
        const content = contentBuilder(item);
        if (!content) return null;
        const date = new Date(item.time);

        return (
          <div className={styles.dataRow} key={`${item.time} ${item.kind}`}>
            {content}
            <div>
              {date.getMonth() + 1}/{date.getDate()}/{date.getFullYear() % 100}
            </div>
          </div>
        );
      })}
    </>
  );
};

export const CustomerJourney = ({ customerJourney }: Props) => {
  if (!customerJourney?.length) {
    return null;
  }
  return (
    <div className={styles.wrapper}>
      <AccordionItem
        icon={<RouteIcon className="contact-details__header-icon" />}
        count={customerJourney?.length}
        title="Customer Journey"
        closeOnOutsideClick={false}
        className="contact-details__accordion-item"
        headingClassName="contact-details__accordion-item-heading"
      >
        <div className={styles.content}>
          {customerJourney?.length ? (
            <CustomerJourneyContent customerJourney={customerJourney} />
          ) : (
            'No data available'
          )}
        </div>
      </AccordionItem>
    </div>
  );
};
